import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, BehaviorSubject, takeUntil, finalize } from 'rxjs';
import { RtaService } from '../../services/rta.service';
import { RTAApiClient } from '../../services/rta-api.service';
import { GlobalNotificationService } from '../../services/globalnotification.service';
import { AssetPlant, MaterialBomItem } from '../../interfaces/plant.interface';
import { SVGIcon, arrowRotateCwIcon, fileExcelIcon } from '@progress/kendo-svg-icons';

@Component({
    selector: 'app-bom-data',
    templateUrl: './bom-data.component.html',
    styleUrls: ['./bom-data.component.css'],
    standalone: false
})
export class BomDataComponent implements OnInit, OnDestroy, OnChanges {
  
  @Input() materialNumber: string = '';
  @Input() readonly: boolean = true;
  
  private destroy$ = new Subject<void>();
  
  // Icons
  public refreshIcon: SVGIcon = arrowRotateCwIcon;
  public excelIcon: SVGIcon = fileExcelIcon;
  
  // Plant selection
  public plants: AssetPlant[] = [];
  public selectedPlant: string = '2130'; // Default plant
  public plantsLoading: boolean = false;
  
  // BOM data
  public bomItems: MaterialBomItem[] = [];
  public bomLoading: boolean = false;
  public bomLoaded: boolean = false;
  public bomError: string = '';
  
  // Grid configuration
  public gridPageSize: number = 20;
  public gridSkip: number = 0;
  
  constructor(
    private rtaService: RtaService,
    private rtaApiClient: RTAApiClient,
    private notificationService: GlobalNotificationService
  ) {}
  
  ngOnInit(): void {
    this.loadPlants();
    if (this.materialNumber && this.materialNumber !== 'Pending') {
      this.loadBom();
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['materialNumber'] && !changes['materialNumber'].firstChange) {
      const newValue = changes['materialNumber'].currentValue;
      if (newValue && newValue !== 'Pending') {
        this.loadBom();
      } else {
        this.bomItems = [];
        this.bomLoaded = false;
        this.bomError = '';
      }
    }
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  /**
   * Loads the list of available manufacturing plants
   * Falls back to default plant if API not available
   */
  loadPlants(): void {
    this.plantsLoading = true;
    this.rtaService.getManufacturingPlants().pipe(
      takeUntil(this.destroy$),
      finalize(() => this.plantsLoading = false)
    ).subscribe({
      next: (response) => {
        if (response?.info && Array.isArray(response.info) && response.info.length > 0) {
          this.plants = response.info as AssetPlant[];
        } else {
          // Use default plant if no plants returned
          this.setDefaultPlant();
        }
      },
      error: (error) => {
        // API not available - use default plant silently
        // This is expected if backend endpoint isn't implemented yet
        this.setDefaultPlant();
      }
    });
  }

  /**
   * Sets a default plant when the API is not available
   */
  private setDefaultPlant(): void {
    this.plants = [
      { id: 1, code: '2130', manufactureFacility: true, enabled: true } as AssetPlant
    ];
    this.selectedPlant = '2130';
  }
  
  /**
   * Loads the BOM for the current material number and selected plant
   */
  loadBom(): void {
    if (!this.materialNumber || this.materialNumber === 'Pending') {
      this.bomError = 'Material number is not available';
      return;
    }
    
    this.bomLoading = true;
    this.bomError = '';
    this.bomItems = [];
    
    // Use RTAApiClient which calls the correct /api/SAPMiddleware/GetMaterialBoM endpoint
    this.rtaApiClient.getMaterialBoM(this.materialNumber, this.selectedPlant).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.bomLoading = false;
        this.bomLoaded = true;
      })
    ).subscribe({
      next: (response) => {
        if (response?.info) {
          // The response.info should contain the BOM items array
          if (Array.isArray(response.info)) {
            this.bomItems = response.info as MaterialBomItem[];
          } else if (response.info.bomItems) {
            this.bomItems = response.info.bomItems as MaterialBomItem[];
          }
          
          if (this.bomItems.length === 0) {
            this.bomError = 'No BOM items found for this material at the selected plant';
          }
        } else {
          this.bomError = 'No BOM data returned';
        }
      },
      error: (error) => {
        console.error('Failed to load BOM:', error);
        this.bomError = 'Failed to load Bill of Materials. Please try again.';
        this.notificationService.showErrorNotification('Failed to load BOM data');
      }
    });
  }
  
  /**
   * Called when the user changes the plant selection
   */
  onPlantChange(event: any): void {
    this.selectedPlant = event;
    if (this.materialNumber && this.materialNumber !== 'Pending') {
      this.loadBom();
    }
  }
  
  /**
   * Refreshes the BOM data
   */
  refreshBom(): void {
    this.loadBom();
  }
  
  /**
   * Exports the BOM to Excel format
   */
  exportToExcel(): void {
    // TODO: Implement Excel export using Kendo ExcelExport
    this.notificationService.showInfoNotification('Excel export coming soon');
  }
  
  /**
   * Gets the total count of BOM items
   */
  get totalItems(): number {
    return this.bomItems.length;
  }
  
  /**
   * Gets the total quantity across all BOM items
   */
  get totalQuantity(): number {
    return this.bomItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }
  
  /**
   * Grid page change handler
   */
  onPageChange(event: { skip: number; take: number }): void {
    this.gridSkip = event.skip;
    this.gridPageSize = event.take;
  }
}
